import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Box } from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import {
  Modal,
  ModalOverflow,
  ModalDialog,
  Stack,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Select,
  Option,
  Autocomplete,
} from "@financeable-com-au/financeable-ui";
import { startCase } from "lodash";
import { APPLICATION_FOLLOWER_ROLES, ERRORS_MESSAGE, SUCCESS_MESSAGE } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { getAllUsersWithBasicInfo, userSelector } from "@/store/slices/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userDetailsSelector, updateApplicationFollowers } from "@/store/slices/applicationFormSlice";
import type { UserSlice, UserState } from "@/store/slices/types/userSlice";
import type { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
import { MiniLoader } from "@components/Loader";

const modalWidth = "520px";

interface ApplicationFollowersModalProps {
  open: boolean;
  onClose: () => void;
  applicationId: string;
}

/** Display shape for a follower (id, name, email, permission) */
interface FollowerDisplay {
  id: string;
  name: string;
  email: string;
  permission: string;
}

function getUserDisplayName(user: {
  firstName?: string;
  lastName?: string;
  email?: { address?: string };
  _id: string;
}): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email?.address || user._id;
}

function getUserEmail(user: { email?: { address?: string } }): string {
  return user.email?.address ?? "";
}

const ApplicationFollowersModal = ({ open, onClose, applicationId }: ApplicationFollowersModalProps) => {
  const dispatch = useDispatch();
  const { application, updatingApplicationFollowers } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const { usersWithBasicInfoList, usersWithBasicInfoLoader } = useSelector<UserSlice, UserState>(userSelector);
  const [followers, setFollowers] = useState<FollowerDisplay[]>([]);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState(APPLICATION_FOLLOWER_ROLES.VIEW);
  const [isAdding, setIsAdding] = useState(false);

  const usersList = usersWithBasicInfoList ?? [];

  const initialFollowers = useMemo((): FollowerDisplay[] => {
    const appFollowers = application?.followers ?? [];
    return appFollowers
      .map((f) => {
        const user = usersList.find((u: { _id: string }) => u._id === f.user);
        if (!user) return null;
        return {
          id: user._id,
          name: getUserDisplayName(user),
          email: getUserEmail(user),
          permission:
            f.permission === APPLICATION_FOLLOWER_ROLES.EDIT
              ? APPLICATION_FOLLOWER_ROLES.EDIT
              : APPLICATION_FOLLOWER_ROLES.VIEW,
        };
      })
      .filter((x): x is FollowerDisplay => x != null);
  }, [application?.followers, usersList]);

  useEffect(() => {
    if (open) {
      setFollowers(initialFollowers);
    }
  }, [open, initialFollowers]);

  useEffect(() => {
    if (open) {
      dispatch(
        getAllUsersWithBasicInfo({
          isAllUsers: false,
          isSelfUser: false,
        }),
      );
    }
  }, [open, dispatch]);

  const availableToAdd = useMemo(() => {
    const followerIds = new Set(followers.map((f) => f.id));
    return usersList.filter((u: { _id: string }) => !followerIds.has(u._id));
  }, [followers, usersList]);

  const userOptions = useMemo(
    () =>
      availableToAdd.map(
        (u: { _id: string; firstName?: string; lastName?: string; email?: { address?: string } }) => ({
          label: `${getUserDisplayName(u)} (${getUserEmail(u)})`,
          value: u._id,
        }),
      ),
    [availableToAdd],
  );

  const selectedUserOption = useMemo(
    () => userOptions.find((o) => o.value === addUserId) ?? null,
    [userOptions, addUserId],
  );

  const handle = {
    addFollower: () => {
      if (!addUserId) return;
      const user = usersList.find((u: { _id: string }) => u._id === addUserId);
      if (!user) return;
      setIsAdding(true);
      setFollowers((prev) => [
        ...prev,
        {
          id: user._id,
          name: getUserDisplayName(user),
          email: getUserEmail(user),
          permission: addRole,
        },
      ]);
      setAddUserId("");
      setAddRole(APPLICATION_FOLLOWER_ROLES.VIEW);
      setIsAdding(false);
    },
    removeFollower: (id: string) => {
      setFollowers((prev) => prev.filter((f) => f.id !== id));
    },
    closeModal: () => {
      setAddUserId("");
      setAddRole(APPLICATION_FOLLOWER_ROLES.VIEW);
      onClose?.();
    },
    saveFollowers: () => {
      const followersToSave = followers.map((f) => ({ user: f.id, permission: f.permission }));

      const updateFollowersPayload = {
        applicationId,
        followers: followersToSave,
      }
      // call api to save followers
      dispatch(updateApplicationFollowers(updateFollowersPayload)).then(({ error, payload }: IDispatchResponse) => {
        if (error) {
          dispatch(openSnackbar({
            message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }));
        } else {
          dispatch(openSnackbar({
            message: payload?.data?.status_message || SUCCESS_MESSAGE.applicationFollowersUpdatedSuccessMsg,
            intent: "success",
          }));
        }
      });
    },
  }

  return (
    <Modal
      aria-labelledby="followers-modal-title"
      aria-describedby="followers-modal-desc"
      open={!!open}
      onClose={handle.closeModal}
    >
      <ModalOverflow>
        <ModalDialog minWidth={modalWidth} maxWidth={modalWidth}>
          <Stack rowGap={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography component="h2" id="followers-modal-title" level="title-md" bold>
                Application Followers
              </Typography>
            </Stack>

            <Typography id="followers-modal-desc" level="body-sm" style={{ color: "text.secondary" }}>
              People with access to this application. You can add or remove followers and set their permission to {startCase(APPLICATION_FOLLOWER_ROLES.VIEW)} or {""}
              {startCase(APPLICATION_FOLLOWER_ROLES.EDIT)}.
            </Typography>

            {/* Current followers list - scrollable after max height */}
            <Stack rowGap={1.5}>
              <Typography level="title-sm">Current followers</Typography>
              {usersWithBasicInfoLoader ? (
                <MiniLoader />
              ) : (
                <>
                  {followers.length === 0 ? (
                    <Box
                      sx={{
                        py: 2,
                        px: 2,
                        borderRadius: 2,
                        bgcolor: "action.hover",
                        textAlign: "center",
                      }}
                    >
                      <Typography level="body-sm" style={{ color: "text.secondary" }}>
                        No followers yet. Add someone below.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflowY: "auto",
                        borderRadius: 2,
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "action.selected", borderRadius: 3 },
                      }}
                    >
                      <Stack rowGap={1}>
                        {followers.map((f) => (
                          <Box
                            key={f.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              py: 0.75,
                              px: 1.5,
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <Stack direction="row" alignItems="center" columnGap={1.5} flex={1} minWidth={0}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography level="body-sm" bold style={{ lineHeight: 1.3 }}>
                                  {f.name}
                                </Typography>
                                <Typography level="body-xs" style={{ color: "text.secondary", lineHeight: 1.3, marginTop: "0.25px" }}>
                                  {f.email}
                                </Typography>
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  px: 1.25,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  bgcolor: f.permission === APPLICATION_FOLLOWER_ROLES.EDIT ? "primary.light" : "action.selected",
                                  color: f.permission === APPLICATION_FOLLOWER_ROLES.EDIT ? "text.white" : "text.secondary",
                                }}
                              >
                                {startCase(f.permission)}
                              </Box>
                            </Stack>
                            <IconButton
                              size="small"
                              onClick={() => handle.removeFollower(f.id)}
                              aria-label={`Remove ${f.name}`}
                              sx={{ ml: 0.5 }}
                            >
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>

            {/* Add follower */}
            <Stack rowGap={2}>
              <Typography level="title-sm">Add follower</Typography>
              <Stack direction="row" columnGap={3} alignItems="flex-end" flexWrap="wrap" rowGap={2}>
                <Box sx={{ minWidth: 180, flex: 1 }}>
                  <FormControl>
                    <FormLabel>User</FormLabel>
                    <Autocomplete
                      placeholder="Search user..."
                      options={userOptions}
                      value={selectedUserOption}
                      onChange={(_e, selection) =>
                        setAddUserId((selection as { label: string; value: string } | null)?.value ?? "")
                      }
                      disabled={usersWithBasicInfoLoader}
                      data-testid="add-follower-user-select"
                    />
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <FormControl>
                    <FormLabel>Permission</FormLabel>
                    <Select
                      value={addRole}
                      size="sm"
                      onChange={(_e, value) => {
                        const permission = value as unknown;
                        setAddRole(permission === APPLICATION_FOLLOWER_ROLES.EDIT || permission === APPLICATION_FOLLOWER_ROLES.VIEW ? permission : APPLICATION_FOLLOWER_ROLES.VIEW);
                      }}
                      data-testid="add-follower-permission-select"
                    >
                      <Option value={APPLICATION_FOLLOWER_ROLES.VIEW}>
                        {startCase(APPLICATION_FOLLOWER_ROLES.VIEW)}
                      </Option>
                      <Option value={APPLICATION_FOLLOWER_ROLES.EDIT}>
                        {startCase(APPLICATION_FOLLOWER_ROLES.EDIT)}
                      </Option>
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handle.addFollower}
                  disabled={!addUserId || isAdding}
                  style={{ minWidth: "72px" }}
                >
                  Add
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" columnGap={3}>
              <Button variant="secondary" onClick={handle.closeModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handle.saveFollowers} disabled={updatingApplicationFollowers}>
                {updatingApplicationFollowers ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default ApplicationFollowersModal;
